import React, { useMemo } from "react";
import { keyframes } from "@emotion/react";
import {
  Box,
  Typography,
  Chip,
  Skeleton,
  Stack,
  Tooltip,
  Avatar,
  alpha,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import BusinessIcon from "@mui/icons-material/Business";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import BuildIcon from "@mui/icons-material/Build";
import PersonIcon from "@mui/icons-material/Person";
import StickyNote2Icon from "@mui/icons-material/StickyNote2";
import GroupsIcon from "@mui/icons-material/Groups";
import ScienceIcon from "@mui/icons-material/Science";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { STATUS_CONFIG } from "../constants";
import { to12Hour, isInternalCalendarEvent, isGhostOccurrence } from "../utils";
import { hourToBusinessMinutes } from "../../../utils/dateUtils";

const EVENT_TYPE_CONFIG = {
  MAINTENANCE: {
    label: "Mantenimiento",
    icon: BuildIcon,
    color: "#1976d2",
  },
  OFFICE_VISIT: {
    label: "Visita oficina",
    icon: BusinessIcon,
    color: "#2e7d32",
  },
  MECHANIC_WORKSHOP: {
    label: "Taller",
    icon: BuildIcon,
    color: "#ed6c02",
  },
  MEETING: {
    label: "Reunión",
    icon: GroupsIcon,
    color: "#9c27b0",
  },
  LAB_STUDY: {
    label: "Laboratorio",
    icon: ScienceIcon,
    color: "#d32f2f",
  },
  UNIT_DELIVERY: {
    label: "Entrega",
    icon: LocalShippingIcon,
    color: "#00838f",
  },
};

const AVATAR_PALETTE = [
  "#2563eb",
  "#0d9488",
  "#7c3aed",
  "#c2410c",
  "#0369a1",
  "#be185d",
  "#15803d",
];

const calRowIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const getInitials = (name = "") => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return parts
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();
};

const colorFromName = (name = "") => {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
};

const GHOST_COLOR = "#94a3b8";
const GHOST_TEXT = "#64748b";

const ServiceRow = ({ event, isSelected, onSelect, showTechnician, index }) => {
  const isGhost = isGhostOccurrence(event);
  const status = STATUS_CONFIG[event.status] || {
    color: "#1976d2",
    label: event.status,
  };
  const rowAccent = isGhost ? GHOST_COLOR : status.color;
  const eventType = event.eventType || "MAINTENANCE";
  const eventConfig = EVENT_TYPE_CONFIG[eventType] || EVENT_TYPE_CONFIG.MAINTENANCE;
  const EventIcon = eventConfig.icon;
  const isEvent = isInternalCalendarEvent(event);
  const hasNote = Boolean(event.note || event.notes);
  const clientName = event.client?.name || event.client || "";
  const branchName = event.branch?.name || event.branch || "";
  const title = isEvent
    ? event.title || eventConfig.label
    : [clientName, branchName].filter(Boolean).join(" · ") ||
      event.type ||
      eventConfig.label;
  const address = event.branch?.address || event.location || "";
  const typeLabel = isEvent
    ? `Evento · ${eventConfig.label}`
    : event.type || eventConfig.label;
  const hourLabel = event.hour ? to12Hour(event.hour) : "—";
  const [hourText, hourPeriod] = hourLabel.includes(" ")
    ? hourLabel.split(" ")
    : [hourLabel, ""];

  return (
    <Box
      component="button"
      type="button"
      onClick={() => onSelect(event)}
      sx={{
        all: "unset",
        boxSizing: "border-box",
        display: "flex",
        alignItems: "stretch",
        width: "100%",
        fontFamily: "inherit",
        color: "inherit",
        cursor: "pointer",
        borderRadius: "12px",
        overflow: "hidden",
        position: "relative",
        bgcolor: isSelected
          ? alpha(rowAccent, 0.12)
          : isGhost
          ? alpha(GHOST_COLOR, 0.08)
          : "#fff",
        border: isGhost ? "1px dashed" : "1px solid",
        borderColor: isSelected
          ? alpha(rowAccent, 0.5)
          : isGhost
          ? alpha(GHOST_COLOR, 0.55)
          : isEvent
          ? alpha("#7c3aed", 0.28)
          : alpha("#0f172a", 0.06),
        opacity: isGhost ? 0.72 : 1,
        filter: isGhost ? "grayscale(0.45)" : "none",
        boxShadow: isSelected
          ? `0 6px 18px ${alpha(rowAccent, 0.18)}`
          : "0 1px 2px rgba(15,23,42,0.04)",
        animation: `${calRowIn} 0.42s cubic-bezier(0.22, 1, 0.36, 1) ${index * 45}ms both`,
        transition:
          "transform 0.22s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.22s ease, border-color 0.22s ease, background-color 0.22s ease",
        "&:hover": {
          transform: isGhost ? "none" : "translateY(-1px) translateX(2px)",
          borderColor: alpha(rowAccent, 0.5),
          boxShadow: isGhost
            ? "0 1px 2px rgba(15,23,42,0.04)"
            : `0 8px 20px ${alpha(rowAccent, 0.16)}`,
          bgcolor: isSelected
            ? alpha(rowAccent, 0.14)
            : isGhost
            ? alpha(GHOST_COLOR, 0.1)
            : alpha(rowAccent, 0.04),
        },
        "&:focus-visible": {
          outline: `2px solid ${rowAccent}`,
          outlineOffset: 2,
        },
      }}
    >
      <Box
        sx={{
          width: 4,
          flexShrink: 0,
          bgcolor: rowAccent,
          transition: "width 0.22s ease",
        }}
      />

      <Box
        sx={{
          width: 72,
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.15,
          px: 0.75,
          py: 0.85,
          bgcolor: isSelected ? alpha(rowAccent, 0.08) : alpha("#0f172a", 0.03),
        }}
      >
        <Typography
          sx={{
            fontSize: "0.78rem",
            fontWeight: 800,
            lineHeight: 1,
            color: isGhost ? GHOST_TEXT : "#0f172a",
            letterSpacing: "-0.03em",
            textAlign: "center",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {hourText}
        </Typography>
        {hourPeriod ? (
          <Typography
            sx={{
              fontSize: "0.58rem",
              fontWeight: 700,
              color: rowAccent,
              letterSpacing: "0.04em",
              lineHeight: 1,
            }}
          >
            {hourPeriod}
          </Typography>
        ) : (
          <AccessTimeIcon sx={{ fontSize: 12, color: rowAccent, opacity: 0.7 }} />
        )}
      </Box>

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          py: 0.85,
          px: 1.15,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: 0.35,
        }}
      >
        <Box display="flex" alignItems="center" gap={0.75} minWidth={0}>
          <Typography
            noWrap
            sx={{
              flex: 1,
              minWidth: 0,
              fontSize: "0.8125rem",
              fontWeight: 700,
              color: isGhost ? GHOST_TEXT : "#0f172a",
              letterSpacing: "-0.01em",
              lineHeight: 1.25,
            }}
          >
            {title}
          </Typography>
          <Chip
            label={isGhost ? "Fantasma" : status.label}
            size="small"
            sx={{
              height: 18,
              fontSize: "0.58rem",
              fontWeight: 700,
              bgcolor: isGhost ? "transparent" : status.color,
              color: isGhost ? GHOST_TEXT : "#fff",
              border: isGhost ? "1px dashed #94a3b8" : "none",
              flexShrink: 0,
              "& .MuiChip-label": { px: 0.7 },
            }}
          />
        </Box>

        <Box display="flex" alignItems="center" gap={0.6} minWidth={0}>
          <EventIcon sx={{ fontSize: 13, color: eventConfig.color, flexShrink: 0 }} />
          <Typography
            noWrap
            sx={{
              fontSize: "0.7rem",
              fontWeight: 600,
              color: alpha("#0f172a", 0.62),
              lineHeight: 1.2,
            }}
          >
            {typeLabel}
          </Typography>
          {showTechnician && (
            <>
              <Box
                sx={{
                  width: 3,
                  height: 3,
                  borderRadius: "50%",
                  bgcolor: alpha("#0f172a", 0.28),
                  flexShrink: 0,
                }}
              />
              <PersonIcon sx={{ fontSize: 12, color: alpha("#0f172a", 0.4) }} />
              <Typography noWrap sx={{ fontSize: "0.68rem", color: alpha("#0f172a", 0.55) }}>
                {event.technician?.name || event.technician || "Sin asignar"}
              </Typography>
            </>
          )}
        </Box>

        {address ? (
          <Box display="flex" alignItems="center" gap={0.45} minWidth={0}>
            <LocationOnIcon sx={{ fontSize: 12, color: alpha("#0f172a", 0.38), flexShrink: 0 }} />
            <Typography
              noWrap
              sx={{
                fontSize: "0.68rem",
                color: alpha("#0f172a", 0.5),
                lineHeight: 1.2,
              }}
            >
              {address}
            </Typography>
          </Box>
        ) : null}
      </Box>

      {hasNote && (
        <Tooltip title={event.note || event.notes} arrow placement="left">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              pr: 1,
              color: "#f59e0b",
            }}
          >
            <StickyNote2Icon sx={{ fontSize: 16 }} />
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

export const ServiceList = ({
  events,
  selectedService,
  onSelectService,
  loading,
  filterType,
  selectedDay,
}) => {
  const shouldGroupByTechnician = filterType !== "technician";

  const groupedEvents = useMemo(() => {
    const sorted = [...(events || [])].sort(
      (a, b) => hourToBusinessMinutes(a.hour) - hourToBusinessMinutes(b.hour)
    );

    if (!shouldGroupByTechnician) {
      return [{ name: "all", items: sorted }];
    }

    const groups = new Map();
    sorted.forEach((event) => {
      const techName = event.technician?.name || event.technician || "Sin asignar";
      if (!groups.has(techName)) groups.set(techName, []);
      groups.get(techName).push(event);
    });

    return Array.from(groups.entries())
      .sort(([a], [b]) => {
        if (a === "Sin asignar") return 1;
        if (b === "Sin asignar") return -1;
        return a.localeCompare(b, "es");
      })
      .map(([name, items]) => ({ name, items }));
  }, [events, shouldGroupByTechnician]);

  if (loading) {
    return (
      <Stack spacing={1} sx={{ py: 0.5 }}>
        {[0, 1, 2, 3].map((i) => (
          <Box key={i} display="flex" gap={1} sx={{ px: 0.25 }}>
            <Skeleton variant="rounded" width={72} height={56} sx={{ borderRadius: "12px" }} />
            <Skeleton variant="rounded" height={56} sx={{ flex: 1, borderRadius: "12px" }} />
          </Box>
        ))}
      </Stack>
    );
  }

  if (!selectedDay) {
    return (
      <Box
        sx={{
          py: 5,
          px: 2,
          textAlign: "center",
          animation: `${calRowIn} 0.4s ease both`,
        }}
      >
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: "16px",
            bgcolor: alpha("#2563eb", 0.08),
            color: "#2563eb",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 1.25,
          }}
        >
          <CalendarMonthIcon />
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", color: "#0f172a" }}>
          Elige un día
        </Typography>
        <Typography sx={{ fontSize: "0.78rem", color: alpha("#0f172a", 0.5), mt: 0.4 }}>
          Selecciona una fecha en el calendario para ver la agenda
        </Typography>
      </Box>
    );
  }

  if (!events?.length) {
    return (
      <Box
        sx={{
          py: 5,
          px: 2,
          textAlign: "center",
          animation: `${calRowIn} 0.4s ease both`,
        }}
      >
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: "16px",
            bgcolor: alpha("#0f172a", 0.05),
            color: alpha("#0f172a", 0.4),
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 1.25,
          }}
        >
          <EventBusyIcon />
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", color: "#0f172a" }}>
          Nada programado
        </Typography>
        <Typography sx={{ fontSize: "0.78rem", color: alpha("#0f172a", 0.5), mt: 0.4 }}>
          No hay servicios ni eventos para esta fecha
        </Typography>
      </Box>
    );
  }

  let rowIndex = 0;

  return (
    <Stack spacing={1.5}>
      {groupedEvents.map((group) => (
        <Box key={group.name}>
          {shouldGroupByTechnician && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 0.75,
                px: 0.25,
              }}
            >
              <Avatar
                sx={{
                  width: 22,
                  height: 22,
                  fontSize: "0.58rem",
                  fontWeight: 800,
                  bgcolor: colorFromName(group.name),
                }}
              >
                {getInitials(group.name)}
              </Avatar>
              <Typography
                noWrap
                sx={{
                  flex: 1,
                  minWidth: 0,
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  color: "#0f172a",
                  letterSpacing: "-0.01em",
                }}
              >
                {group.name}
              </Typography>
              <Chip
                label={group.items.length}
                size="small"
                sx={{
                  height: 18,
                  minWidth: 22,
                  fontSize: "0.62rem",
                  fontWeight: 700,
                  bgcolor: alpha("#0f172a", 0.06),
                  color: alpha("#0f172a", 0.7),
                  "& .MuiChip-label": { px: 0.6 },
                }}
              />
            </Box>
          )}
          <Stack spacing={0.75}>
            {group.items.map((event) => {
              const index = rowIndex;
              rowIndex += 1;
              return (
                <ServiceRow
                  key={`${event.originalServiceId || event._id}-${event.technician?._id || "none"}`}
                  event={event}
                  isSelected={selectedService?._id === event._id}
                  onSelect={onSelectService}
                  showTechnician={!shouldGroupByTechnician}
                  index={index}
                />
              );
            })}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
};
